/**
 * Sign-in for the /calendar page, built from the shared PKCE primitives in ../auth/sso. The
 * Keycloak client registers wildcard redirect URIs on the SPA origin, so the callback can land
 * on /calendar?sso=callback instead of a room URL; the flow state rides the per-tab
 * vc.ssoFlow sessionStorage key (the `room` field carries the 'calendar' marker) while the
 * session pair lives in the tab-shared vc.ssoSession localStorage key (see ../auth/sso). The
 * session stores the token pair exactly like the prejoin path, so the auto-refresh scheduler
 * and the 401-recovery wrapper in ../auth/sso keep it alive while the tab is open. When the
 * rotation chain dies anyway (invalid_grant), attemptSilentRelogin starts one PKCE redirect
 * the realm's SSO cookie usually answers without any user interaction.
 */

import {
  buildSsoSession,
  clearSsoFlow,
  clearSsoSession,
  codeChallengeS256,
  exchangeCodeForTokens,
  generateCodeVerifier,
  generateState,
  loadSsoFlow,
  loadSsoSession,
  parseSsoCallback,
  saveSsoFlow,
  saveSsoSession,
  SSO_CALLBACK_MARKER,
  ssoConfigFromEnv,
  type SsoConfig,
} from '../auth/sso';
import type { KeyValueStorage } from '../room/devicePrefs';

/** Marker stored in the flow state's `room` field for calendar-initiated sign-ins. */
export const CALENDAR_FLOW_ROOM = 'calendar';

/** Path the Keycloak redirect comes back to, relative to the SPA origin. */
export const CALENDAR_REDIRECT_PATH = '/calendar';

/** Where a missing or expired session cannot be told apart from a network failure. */
export type CalendarSessionCheck = 'signed_in' | 'expired' | 'guest';

/**
 * Classifies the stored session against `now`: signed_in while the access-token expiry stored
 * with the session is more than 30 s away (leeway covers the round-trip), expired once past
 * it, guest when nothing usable is stored. A session without a parseable shape (legacy
 * access-token-only entries) counts as guest - the strict loader drops it.
 */
export function checkSession(
  storage: KeyValueStorage,
  now: Date = new Date(),
): CalendarSessionCheck {
  const session = loadSsoSession(storage);
  if (session === null) {
    return 'guest';
  }
  return session.accessExpiresAt > now.getTime() + 30_000 ? 'signed_in' : 'expired';
}

/**
 * Starts the calendar sign-in: PKCE verifier + CSRF state into sessionStorage, then a full
 * redirect to Keycloak with redirect_uri = <origin><returnPath>?sso=callback. The return path
 * defaults to the calendar page; other SPA surfaces (the /dashboard) pass their own route -
 * the Keycloak client allows any path of the SPA origin. Resolves with the authorize URL after
 * the redirect has been issued.
 */
export async function startCalendarSignIn(
  config: SsoConfig,
  origin: string,
  storage: KeyValueStorage,
  target: { assign(href: string): void } = window.location,
  returnPath: string = CALENDAR_REDIRECT_PATH,
): Promise<string> {
  const verifier = generateCodeVerifier();
  const challenge = await codeChallengeS256(verifier);
  const state = generateState();
  saveSsoFlow(storage, { room: CALENDAR_FLOW_ROOM, verifier, state });
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: `${origin}${returnPath}?${SSO_CALLBACK_MARKER}`,
    scope: 'openid',
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });
  const href = `${config.baseUrl}/realms/${config.realm}/protocol/openid-connect/auth?${params.toString()}`;
  target.assign(href);
  return href;
}

/** Outcome of completing a calendar sign-in from the callback query. */
export type CalendarSignInOutcome =
  | { readonly kind: 'signed_in'; readonly accessToken: string; readonly username: string }
  | { readonly kind: 'stale_state' }
  | { readonly kind: 'failed'; readonly message: string };

/** Display name decoded from the token payload; falls back like the shared module. */
export function sessionUsername(accessToken: string): string {
  return usernameFromToken(accessToken);
}

function usernameFromToken(accessToken: string): string {
  const segments = accessToken.split('.');
  const payloadSegment = segments[1];
  if (segments.length !== 3 || payloadSegment === undefined) {
    return 'SSO user';
  }
  try {
    const padded = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const parsed: unknown = JSON.parse(atob(padded + '='.repeat((4 - (padded.length % 4)) % 4)));
    if (typeof parsed === 'object' && parsed !== null) {
      const name = (parsed as Record<string, unknown>).preferred_username;
      if (typeof name === 'string' && name.length > 0) {
        return name;
      }
    }
  } catch {
    // fall through to the fallback name
  }
  return 'SSO user';
}

/**
 * Completes a sign-in from the ?sso=callback query: validates the CSRF state against the stored
 * flow, exchanges the code, stores the fresh access token and reports the display name. The
 * return path must match the one startCalendarSignIn redirected to (the token endpoint checks
 * the redirect_uri); terminal on every outcome, stripping the query stays the caller's job.
 */
export async function completeCalendarSignIn(
  config: SsoConfig,
  callback: { readonly code: string; readonly state: string },
  origin: string,
  storage: KeyValueStorage,
  fetchImpl: typeof fetch = globalThis.fetch,
  returnPath: string = CALENDAR_REDIRECT_PATH,
): Promise<CalendarSignInOutcome> {
  const flow = loadSsoFlow(storage);
  if (flow === null || flow.state !== callback.state || flow.room !== CALENDAR_FLOW_ROOM) {
    clearSsoFlow(storage);
    return { kind: 'stale_state' };
  }
  try {
    const tokens = await exchangeCodeForTokens(
      config,
      {
        code: callback.code,
        verifier: flow.verifier,
        redirectUri: `${origin}${returnPath}?${SSO_CALLBACK_MARKER}`,
      },
      fetchImpl,
    );
    clearSsoFlow(storage);
    saveSsoSession(storage, buildSsoSession(tokens));
    return {
      kind: 'signed_in',
      accessToken: tokens.accessToken,
      username: usernameFromToken(tokens.accessToken),
    };
  } catch (err: unknown) {
    clearSsoFlow(storage);
    return {
      kind: 'failed',
      message:
        err instanceof Error && err.message.length > 0 ? err.message : 'token exchange failed',
    };
  }
}

/** Parses the current query for a calendar callback (delegates to the shared parser). */
export function parseCalendarCallback(query: string): ReturnType<typeof parseSsoCallback> {
  return parseSsoCallback(query);
}

/**
 * Clears a session the API has rejected (401): the page flips to the re-sign-in gate. Sign-out
 * (v1) revokes nothing server-side - the refresh token stays valid until the realm's SSO idle
 * timeout expires it, but it is unusable without this browser's storage.
 */
export function forgetSession(storage: KeyValueStorage): void {
  clearSsoSession(storage);
  clearSsoFlow(storage);
}

// --- Silent re-login after a dead rotation chain ---------------------------------------------------

/** Storage key (per-tab) holding the epoch ms of the last automatic silent re-login redirect. */
export const SSO_AUTO_RELOGIN_FLAG = 'vc.ssoAutoReloginAt';

/** Minimum spacing between automatic silent re-login redirects (the loop breaker). */
const AUTO_RELOGIN_GUARD_MS = 30_000;

/**
 * True when this tab already auto-redirected into a silent re-login within the guard window:
 * a second gate-after-attempt this soon means the redirect is looping (the realm rejected the
 * silent sign-in too), so the caller must show the expired screen instead of bouncing again.
 */
export function silentReloginLooping(storage: KeyValueStorage, nowMs: number): boolean {
  const raw = storage.getItem(SSO_AUTO_RELOGIN_FLAG);
  const at = raw === null ? Number.NaN : Number(raw);
  return Number.isFinite(at) && nowMs - at < AUTO_RELOGIN_GUARD_MS;
}

/**
 * The calendar-level answer to a refresh ending invalid_grant: drops the dead session and
 * starts the EXISTING PKCE sign-in redirect - the realm's SSO cookie (10 h) makes it silent,
 * so the user comes back signed in without typing anything. Guarded by SSO_AUTO_RELOGIN_FLAG:
 * at most one auto-redirect per 30 s per tab, so a fast failure loop settles on the expired
 * gate. The check and the flag write run synchronously, so concurrent gate-after-attempt
 * callbacks issue exactly one redirect. Returns false (and does nothing) when the guard
 * blocks the redirect or the sign-in could not be started.
 */
export async function attemptSilentRelogin(
  config: SsoConfig,
  origin: string,
  storage: KeyValueStorage,
  target: { assign(href: string): void } = window.location,
  nowMs: number = Date.now(),
): Promise<boolean> {
  if (silentReloginLooping(storage, nowMs)) {
    return false;
  }
  try {
    storage.setItem(SSO_AUTO_RELOGIN_FLAG, String(nowMs));
  } catch {
    // Without the flag a redirect cannot be bounded - stay on the gate instead of looping.
    return false;
  }
  forgetSession(storage);
  try {
    await startCalendarSignIn(config, origin, storage, target);
  } catch {
    return false;
  }
  return true;
}

/** The SSO config resolved from the build environment (stand defaults underneath). */
export function calendarSsoConfig(): SsoConfig {
  return ssoConfigFromEnv();
}
